import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { useTranslation } from 'react-i18next'
import EditIcon from '@mui/icons-material/Edit'
import TableBackdrop from '../../../TableUI/TableBackdrop'
import Pagination from '../../../Pagination/Pagination'
import { Stack, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { COUNTRY_SPREADS__UPDATE } from '../../../../enums/Caps'
import useHasPermissions from '../../../../hooks/useHasPermissions'
import IconButton from '@mui/material/IconButton'
import { useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import TextField from '@mui/material/TextField'
import useAxiosPrivate from 'hooks/useAxiosPrivate'
import { toast } from 'react-toastify'


function Per({ children, permission }) {
    const hasPermissions = useHasPermissions()
    const hasPermission = hasPermissions(permission)
    return hasPermission ? children : null
}


function percentOrDash(value) {
    return (value !== null) ? `${value}%` : '-'
}


function EditButton({ handleClick }) {
    return <IconButton
        aria-label="edit"
        onClick={handleClick}
    >
        <EditIcon />
    </IconButton>
}


function SpreadRow({ data, merchantId }) {
    const [editing, setEditing] = useState(false)
    const [merchantSpread, setMerchantSpread] = useState(data.merchant_spread)
    const [savedMerchantSpread, setSavedMerchantSpread] = useState(data.merchant_spread)
    const [waitingResponse, setWaitingResponse] = useState(false)
    const api = useAxiosPrivate()
    const { t } = useTranslation()
    return <TableRow
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        style={{ opacity: waitingResponse ? 0.5 : 1 }}
    >
        <TableCell component="th" scope="row">
            <Typography noWrap>{data.country}</Typography>
        </TableCell>
        <TableCell style={{ opacity: (merchantSpread === null) ? 1.0 : 0.5 }}>
            {percentOrDash(data.global_spread)}
        </TableCell>
        <TableCell>
            {editing ?
                <Stack direction="row" alignItems="center">
                    <TextField
                        id="merchant_spread"
                        type="number"
                        value={merchantSpread ?? ''}
                        onChange={(e) => setMerchantSpread(e.target.value === '' ? null : e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <IconButton onClick={() => { setMerchantSpread(null) }}><DeleteIcon /></IconButton>
                </Stack> :
                percentOrDash(merchantSpread)
            }
        </TableCell>
        <TableCell align='right'>
            {editing ?
                <>
                    <IconButton onClick={() => { setMerchantSpread(savedMerchantSpread); setEditing(false) }}>
                        <CancelIcon style={{ fill: "red" }} />
                    </IconButton>
                    <IconButton onClick={() => {
                        api.put(`/internal/merchants/${merchantId}/spreads/${data.country}/`, { spread: merchantSpread })
                            .then(({ data: { data } }) => {
                                setSavedMerchantSpread(data.spread)
                                setMerchantSpread(data.spread)
                            }).catch((error) => {
                                setMerchantSpread(savedMerchantSpread)
                                toast.error(t("Error updating spread"))
                            }).finally(() => {
                                setEditing(false)
                                setWaitingResponse(false)
                            })
                        setWaitingResponse(true)
                    }}>
                        <CheckCircleIcon style={{ fill: "green" }} />
                    </IconButton>
                </> :
                <Per permission={COUNTRY_SPREADS__UPDATE}><EditButton handleClick={() => setEditing(true)} /></Per>
            }
        </TableCell>
    </TableRow >
}


export default function SpreadsTable({
    data,
    pages = 1,
    page = 1,
    loading = false,
    handleOnPageChange,
    merchantId
}) {
    const { t } = useTranslation()

    return (
        <>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('Country')}</TableCell>
                            <TableCell>{t('Global Spread')}</TableCell>
                            <TableCell>{t('Merchant Spread')}</TableCell>
                            <TableCell align="center"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length ? data.map((row, idx) => (
                            <SpreadRow key={idx} data={row} merchantId={merchantId} />
                        )) : <TableRow>
                            {
                                loading ? <TableBackdrop open={loading} /> : <TableCell sx={{ textAlign: 'center' }} colSpan={100}>{t('No records found')}</TableCell>
                            }
                        </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <Pagination
                handleOnPageChange={handleOnPageChange}
                page={page}
                pages={pages}
            />
        </>
    )
}
